<div class="md:pt-3 pt-2 md:px-4 px-3 pb-8 mb-4">
  <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
    {{ isEditMode ? ('COMMISSION_MANAGEMENT.RULES.EDIT' | translate) : ('COMMISSION_MANAGEMENT.RULES.CREATE' | translate) }}
  </h1>
  <div class="mt-1 mb-3">
    <app-breadcrumb />
  </div>

  <div class="card">
    <!-- Header -->
    <div class="flex justify-content-between align-items-center mb-4 pb-3 border-bottom-1 surface-border">
      <div>
        <p class="text-600 mt-0 mb-0">
          {{ isEditMode ? ('COMMISSION_MANAGEMENT.RULES.EDIT_DESC' | translate) : ('COMMISSION_MANAGEMENT.RULES.CREATE_DESC' | translate) }}
        </p>
      </div>
      <p-button
        [label]="'COMMISSION_MANAGEMENT.COMMON.BACK' | translate"
        icon="pi pi-arrow-left"
        [outlined]="true"
        (onClick)="goBack()"
        aria-label="back-button" />
    </div>

    <form [formGroup]="ruleForm" class="grid">

      <!-- ─────────────────────────────────────────────────────────────
           Section 1: Rule Category
      ───────────────────────────────────────────────────────────────── -->
      <div class="col-12">
        <div class="surface-50 border-round-lg p-3 mb-3">
          <div class="flex align-items-center gap-3 mb-3">
            <div class="flex align-items-center justify-content-center bg-primary border-circle font-semibold text-white"
              style="width: 2rem; height: 2rem; font-size: 0.875rem;">
              1
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-900 m-0">
                {{ 'COMMISSION_MANAGEMENT.RULES.CATEGORY_SECTION_TITLE_SHORT' | translate }}
              </h3>
              <p class="text-600 text-xs m-0 mt-1">
                {{ 'COMMISSION_MANAGEMENT.RULES.CATEGORY_SECTION_DESC' | translate }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            @for (option of categoryOptions; track option) {
              @if (!option.hidden) {
                <div class="flex align-items-center surface-0 border-1 surface-border border-round-md px-3 py-2
                            hover:surface-100 transition-colors transition-duration-150 cursor-pointer"
                  [class.border-primary]="ruleForm.get('category')?.value === option.value"
                  [class.surface-100]="ruleForm.get('category')?.value === option.value"
                  (click)="ruleForm.get('category')?.setValue(option.value); onCategoryChange()">
                  <p-radiobutton
                    [inputId]="'category-' + option.value"
                    [value]="option.value"
                    formControlName="category"
                    [ariaLabel]="'category-' + option.value" />
                  <label [for]="'category-' + option.value"
                    class="ml-2 font-medium text-900 text-sm white-space-nowrap cursor-pointer"
                    (click)="$event.preventDefault()">
                    {{ 'COMMISSION_MANAGEMENT.CATEGORIES.' + option.value.toUpperCase() | translate }}
                  </label>
                </div>
              }
            }
          </div>

          @if (ruleForm.get('category')?.invalid && ruleForm.get('category')?.touched) {
            <small class="p-error block mt-2">
              <i class="pi pi-exclamation-circle mr-1"></i>
              {{ 'COMMISSION_MANAGEMENT.RULES.CATEGORY_REQUIRED' | translate }}
            </small>
          }
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           Section 2: Scope Type
      ───────────────────────────────────────────────────────────────── -->
      <div class="col-12">
        <div class="border-round-lg p-3 mb-3"
          [class.surface-50]="isCategorySelected"
          [class.surface-100]="!isCategorySelected"
          [class.opacity-60]="!isCategorySelected">
          <div class="flex align-items-center gap-3 mb-3">
            <div class="flex align-items-center justify-content-center border-circle font-semibold text-white"
              [class.bg-primary]="isCategorySelected"
              [class.surface-400]="!isCategorySelected"
              style="width: 2rem; height: 2rem; font-size: 0.875rem;">
              2
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-900 m-0">
                {{ 'COMMISSION_MANAGEMENT.RULES.SCOPE_TYPE_SECTION_TITLE_SHORT' | translate }}
              </h3>
              <p class="text-600 text-xs m-0 mt-1">
                {{ 'COMMISSION_MANAGEMENT.RULES.SCOPE_TYPE_SECTION_DESC' | translate }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            @for (option of scopeTypeOptions; track option) {
              <div class="flex align-items-center surface-0 border-1 surface-border border-round-md px-3 py-2
                          hover:surface-100 transition-colors transition-duration-150 cursor-pointer"
                [class.border-primary]="ruleForm.get('scope_type')?.value === option.value"
                [class.surface-100]="ruleForm.get('scope_type')?.value === option.value"
                (click)="ruleForm.get('scope_type')?.setValue(option.value); onScopeTypeChange()">
                <p-radiobutton
                  [inputId]="'scope-type-' + option.value"
                  [value]="option.value"
                  formControlName="scope_type"
                  [ariaLabel]="'scope-type-' + option.value" />
                <label [for]="'scope-type-' + option.value"
                  class="ml-2 cursor-pointer font-medium text-900 text-sm white-space-nowrap"
                  (click)="$event.preventDefault()">
                  {{ 'COMMISSION_MANAGEMENT.SCOPE_TYPES.' + option.value.toUpperCase() | translate }}
                </label>
              </div>
            }
          </div>

          @if (ruleForm.get('scope_type')?.invalid && ruleForm.get('scope_type')?.touched) {
            <small class="p-error block mt-2">
              <i class="pi pi-exclamation-circle mr-1"></i>
              {{ 'COMMISSION_MANAGEMENT.RULES.SCOPE_TYPE_REQUIRED' | translate }}
            </small>
          }
        </div>
      </div>

      <!-- Scope Selection: Subscription Plans -->
      @if (ruleForm.get('scope_type')?.value === scopeTypeEnum.SubscriptionPlan) {
        <div class="col-12">
          <div class="surface-50 border-round-lg p-3 mb-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-calendar text-primary"></i>
              <label for="scope_plans" class="font-semibold text-900 text-sm m-0">
                {{ 'COMMISSION_MANAGEMENT.RULES.SELECT_PLANS' | translate }}
              </label>
            </div>
            <p-multiselect
              inputId="scope_plans"
              formControlName="scope_plans"
              [options]="subscriptionPlans"
              optionLabel="name"
              optionValue="name"
              [filter]="true"
              [placeholder]="'COMMISSION_MANAGEMENT.RULES.SELECT_PLANS_PLACEHOLDER' | translate"
              [showClear]="true"
              class="w-full"
              display="chip"
              [loading]="loadingPlans"
              styleClass="w-full"
              (onChange)="onScopeSelectionChange()"
              aria-label="select-subscription-plans">
              <ng-template let-plan pTemplate="item">
                <div class="flex align-items-center gap-2">
                  <span>{{ plan.name }}</span>
                </div>
              </ng-template>
            </p-multiselect>
          </div>
        </div>
      }

      <!-- Scope Selection: Service Categories -->
      @if (ruleForm.get('scope_type')?.value === scopeTypeEnum.ServiceCategory) {
        <div class="col-12">
          <div class="surface-50 border-round-lg p-3 mb-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-briefcase text-cyan-500"></i>
              <label for="scope_services" class="font-semibold text-900 text-sm m-0">
                {{ 'COMMISSION_MANAGEMENT.RULES.SELECT_SERVICES' | translate }}
              </label>
            </div>
            <p-multiselect
              inputId="scope_services"
              formControlName="scope_services"
              [options]="filteredServiceCategories"
              optionLabel="name"
              optionValue="name"
              [filter]="true"
              [placeholder]="'COMMISSION_MANAGEMENT.RULES.SELECT_SERVICES_PLACEHOLDER' | translate"
              [showClear]="true"
              class="w-full"
              display="chip"
              [loading]="loadingServices"
              styleClass="w-full"
              (onChange)="onScopeSelectionChange()"
              aria-label="select-service-categories">
              <ng-template let-service pTemplate="item">
                <div class="flex align-items-center gap-2">
                  <span>{{ service.name }}</span>
                </div>
              </ng-template>
            </p-multiselect>
          </div>
        </div>
      }

      <!-- Scope Selection: Retail Items -->
      @if (ruleForm.get('scope_type')?.value === scopeTypeEnum.RetailItem) {
        <div class="col-12">
          <div class="surface-50 border-round-lg p-3 mb-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-shopping-cart text-orange-500"></i>
              <label for="scope_products" class="font-semibold text-900 text-sm m-0">
                {{ 'COMMISSION_MANAGEMENT.RULES.SELECT_PRODUCTS' | translate }}
              </label>
            </div>
            <p-multiselect
              inputId="scope_products"
              formControlName="scope_products"
              [options]="retailProducts"
              optionLabel="name"
              optionValue="id"
              [filter]="true"
              [placeholder]="'COMMISSION_MANAGEMENT.RULES.SELECT_PRODUCTS_PLACEHOLDER' | translate"
              [showClear]="true"
              class="w-full"
              display="chip"
              [loading]="loadingProducts"
              styleClass="w-full"
              (onChange)="onScopeSelectionChange()"
              aria-label="select-retail-products">
              <ng-template let-product pTemplate="item">
                <div class="flex align-items-center gap-2">
                  <span>{{ product.name }}</span>
                </div>
              </ng-template>
            </p-multiselect>

            <!-- Toggle: Limit Specific SKUs -->
            <div class="flex align-items-center justify-content-between mt-3 p-2 surface-0 border-1 surface-border border-round">
              <label for="limit_skus" class="font-medium text-900 text-sm cursor-pointer">
                {{ 'COMMISSION_MANAGEMENT.RULES.LIMIT_SPECIFIC_SKUS' | translate }}
              </label>
              <p-toggleswitch
                inputId="limit_skus"
                formControlName="limit_specific_skus"
                (onChange)="onLimitSkusChange()"
                aria-label="limit-specific-skus-toggle" />
            </div>

            <!-- SKU Multiselect -->
            @if (ruleForm.get('limit_specific_skus')?.value && selectedProductIds.length > 0) {
              <div class="mt-2">
                <p-multiselect
                  inputId="scope_skus"
                  formControlName="scope_skus"
                  [options]="productSkus"
                  optionLabel="name"
                  optionValue="sku_name"
                  [filter]="true"
                  [placeholder]="'COMMISSION_MANAGEMENT.RULES.SELECT_SKUS_PLACEHOLDER' | translate"
                  [showClear]="true"
                  class="w-full"
                  display="chip"
                  [loading]="loadingSkus"
                  styleClass="w-full"
                  aria-label="select-product-skus">
                  <ng-template let-sku pTemplate="item">
                    <div class="flex flex-column gap-1">
                      <span class="font-semibold text-sm">{{ sku.sku_name }}</span>
                      <span class="text-xs text-600">{{ sku.product_name }}</span>
                    </div>
                  </ng-template>
                </p-multiselect>
              </div>
            }
          </div>
        </div>
      }

      <!-- ─────────────────────────────────────────────────────────────
           Section 3: Flat Commission Values
      ───────────────────────────────────────────────────────────────── -->
      <div class="col-12">
        <div class="border-round-lg p-3 mb-3"
          [class.surface-50]="isScopeValid"
          [class.surface-100]="!isScopeValid"
          [class.opacity-60]="!isScopeValid">
          <div class="flex align-items-center gap-3 mb-3">
            <div class="flex align-items-center justify-content-center border-circle font-semibold text-white"
              [class.bg-primary]="isScopeValid"
              [class.surface-400]="!isScopeValid"
              style="width: 2rem; height: 2rem; font-size: 0.875rem;">
              3
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-900 m-0">
                {{ 'COMMISSION_MANAGEMENT.PLANS.FLAT_VALUES' | translate }}
              </h3>
              <p class="text-600 text-xs m-0 mt-1">
                {{ 'COMMISSION_MANAGEMENT.PLANS.FLAT_VALUES_DESC' | translate }}
              </p>
            </div>
          </div>

          <div class="grid">
            <!-- Flat Trial Value -->
            <div class="col-12 md:col-6 lg:col-3">
              <label for="flat_trial_value" class="font-semibold text-900 text-sm">
                {{ 'COMMISSION_MANAGEMENT.PLANS.FLAT_TRIAL_VALUE' | translate }}
              </label>
              <p-inputNumber
                inputId="flat_trial_value"
                formControlName="flat_trial_value"
                mode="currency"
                [currency]="currencyCode"
                [locale]="locale"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                class="w-full mt-2"
                placeholder="0.00"
                aria-label="flat-trial-value-input" />
            </div>

            <!-- Flat Free Value -->
            <div class="col-12 md:col-6 lg:col-3">
              <label for="flat_free_value" class="font-semibold text-900 text-sm">
                {{ 'COMMISSION_MANAGEMENT.PLANS.FLAT_FREE_VALUE' | translate }}
              </label>
              <p-inputNumber
                inputId="flat_free_value"
                formControlName="flat_free_value"
                mode="currency"
                [currency]="currencyCode"
                [locale]="locale"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                class="w-full mt-2"
                placeholder="0.00"
                aria-label="flat-free-value-input" />
            </div>

            <!-- Flat Comp Value -->
            <div class="col-12 md:col-6 lg:col-3">
              <label for="flat_comp_value" class="font-semibold text-900 text-sm">
                {{ 'COMMISSION_MANAGEMENT.PLANS.FLAT_COMP_VALUE' | translate }}
              </label>
              <p-inputNumber
                inputId="flat_comp_value"
                formControlName="flat_comp_value"
                mode="currency"
                [currency]="currencyCode"
                [locale]="locale"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                class="w-full mt-2"
                placeholder="0.00"
                aria-label="flat-comp-value-input" />
            </div>

            <!-- No-show Value in Percent -->
            <div class="col-12 md:col-6 lg:col-3">
              <label for="noshow_value_in_percent" class="font-semibold text-900 text-sm">
                {{ 'COMMISSION_MANAGEMENT.PLANS.NOSHOW_PERCENT' | translate }}
              </label>
              <p-inputNumber
                inputId="noshow_value_in_percent"
                formControlName="noshow_value_in_percent"
                mode="decimal"
                [min]="0"
                [max]="100"
                suffix="%"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                class="w-full mt-2"
                placeholder="0%"
                aria-label="noshow-percent-input" />
            </div>
          </div>
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           Section 4: Commission Rate Configuration + Slab Table
      ───────────────────────────────────────────────────────────────── -->
      <div class="col-12">
        <div class="border-round-lg p-3 mb-3"
          [class.surface-50]="isScopeValid"
          [class.surface-100]="!isScopeValid"
          [class.opacity-60]="!isScopeValid">

          <!-- Section header + rate type toggle -->
          <div class="flex align-items-center gap-3 mb-3">
            <div class="flex align-items-center justify-content-center border-circle font-semibold text-white"
              [class.bg-primary]="isScopeValid"
              [class.surface-400]="!isScopeValid"
              style="width: 2rem; height: 2rem; font-size: 0.875rem;">
              4
            </div>
            <div class="flex-1 flex align-items-center justify-content-between">
              <div>
                <h3 class="text-lg font-semibold text-900 m-0">
                  {{ 'COMMISSION_MANAGEMENT.RULES.RATE_CONFIG_TITLE' | translate }}
                </h3>
                <p class="text-600 text-xs m-0 mt-1">
                  {{ 'COMMISSION_MANAGEMENT.RULES.RATE_CONFIG_DESC' | translate }}
                </p>
              </div>
              <!-- Rate Type Toggle: Flat Fee ↔ Percentage -->
              <div class="flex align-items-center gap-3 ml-4">
                <span class="font-medium text-900 text-sm" [class.text-primary]="!isPercentageRate">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.FLAT_FEE' | translate }}
                </span>
                <p-toggleswitch
                  [(ngModel)]="isPercentageRate"
                  [ngModelOptions]="{standalone: true}"
                  (onChange)="onRateTypeToggleChange()"
                  aria-label="rate-type-toggle" />
                <span class="font-medium text-900 text-sm" [class.text-primary]="isPercentageRate">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.PERCENTAGE' | translate }}
                </span>
              </div>
            </div>
          </div>

          <!-- Slab Table -->
          @if (isScopeValid && slabColumns.length > 0) {
            <div class="mt-3">
              <p-table
                [value]="slabRows"
                [scrollable]="true"
                scrollHeight="400px"
                [frozenColumns]="frozenColumns"
                styleClass="p-datatable-sm p-datatable-gridlines"
                aria-label="slab-configuration-table">

                <ng-template pTemplate="header">
                  <tr>
                    <th pFrozenColumn style="min-width: 130px;"
                      class="text-center font-semibold text-900 text-sm surface-100">
                      {{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_FROM' | translate }}
                    </th>
                    <th pFrozenColumn style="min-width: 130px;"
                      class="text-center font-semibold text-900 text-sm surface-100">
                      {{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_TO' | translate }}
                    </th>
                    @for (col of slabColumns; track col) {
                      <th style="min-width: 150px;"
                        class="text-center font-semibold text-900 text-sm surface-100">
                        {{ col }}
                      </th>
                    }
                    <th style="min-width: 80px;"
                      class="text-center font-semibold text-900 text-sm surface-100">
                      {{ 'COMMISSION_MANAGEMENT.COMMON.ACTIONS' | translate }}
                    </th>
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                  <tr>
                    <!-- Quantity From (frozen) -->
                    <td pFrozenColumn class="p-1">
                      <p-inputNumber
                        [(ngModel)]="row['quantity_from']"
                        [ngModelOptions]="{standalone: true}"
                        [min]="0"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="0"
                        class="w-full"
                        styleClass="w-full"
                        placeholder="0"
                        [ariaLabel]="'quantity-from-' + rowIndex" />
                    </td>
                    <!-- Quantity To (frozen) -->
                    <td pFrozenColumn class="p-1">
                      <p-inputNumber
                        [(ngModel)]="row['quantity_to']"
                        [ngModelOptions]="{standalone: true}"
                        [min]="0"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="0"
                        class="w-full"
                        styleClass="w-full"
                        placeholder="0"
                        [ariaLabel]="'quantity-to-' + rowIndex" />
                    </td>
                    <!-- Dynamic scope columns -->
                    @for (col of slabColumns; track col) {
                      <td class="p-1">
                        @if (isPercentageRate) {
                          <p-inputNumber
                            [(ngModel)]="row[col]"
                            [ngModelOptions]="{standalone: true}"
                            [min]="0"
                            [max]="100"
                            suffix="%"
                            [minFractionDigits]="0"
                            [maxFractionDigits]="2"
                            class="w-full"
                            styleClass="w-full"
                            placeholder="0%"
                            [ariaLabel]="'rate-' + col + '-' + rowIndex" />
                        } @else {
                          <p-inputNumber
                            [(ngModel)]="row[col]"
                            [ngModelOptions]="{standalone: true}"
                            [min]="0"
                            [minFractionDigits]="2"
                            [maxFractionDigits]="2"
                            class="w-full"
                            styleClass="w-full"
                            placeholder="0.00"
                            [ariaLabel]="'rate-' + col + '-' + rowIndex" />
                        }
                      </td>
                    }
                    <!-- Delete row -->
                    <td class="text-center p-1">
                      <p-button
                        icon="pi pi-trash"
                        severity="danger"
                        [text]="true"
                        [rounded]="true"
                        (onClick)="removeSlabRow(rowIndex)"
                        [ariaLabel]="'remove-slab-row-' + rowIndex" />
                    </td>
                  </tr>
                </ng-template>

                <ng-template pTemplate="footer">
                  <tr>
                    <td [attr.colspan]="slabColumns.length + 3" class="p-2">
                      <p-button
                        [label]="'COMMISSION_MANAGEMENT.SLABS.ADD_MORE_SLAB' | translate"
                        icon="pi pi-plus"
                        [text]="true"
                        (onClick)="addSlabRow()"
                        aria-label="add-slab-row-button" />
                    </td>
                  </tr>
                </ng-template>

              </p-table>
            </div>
          }

        </div>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           Form Actions
      ───────────────────────────────────────────────────────────────── -->
      <div class="col-12">
        <div class="flex justify-content-end gap-2 mt-3 pt-3 border-top-1 surface-border">
          <p-button
            [label]="'COMMISSION_MANAGEMENT.COMMON.CANCEL' | translate"
            icon="pi pi-times"
            [outlined]="true"
            severity="secondary"
            (onClick)="goBack()"
            aria-label="cancel-button" />
          <p-button
            [label]="'COMMISSION_MANAGEMENT.COMMON.SAVE' | translate"
            icon="pi pi-check"
            [loading]="saving"
            [disabled]="ruleForm.invalid"
            (onClick)="saveRule()"
            aria-label="save-rule-button" />
        </div>
      </div>

    </form>
  </div>
</div>
